// Identity storage + accessors shared by the auth context and the API client.
// The client cannot use React hooks, so the current identity is mirrored into a
// module-level variable (kept in sync by the AuthProvider) and localStorage.

export type AuthMode = 'dev' | 'prod';

export interface Identity {
  // Dev mode: a UUID + role sent as X-User-Id / X-Role.
  userId: string;
  role: string;
  // Prod mode: a bearer token sent as Authorization.
  token?: string;
  // Prod mode: the signed-in person's email from the OIDC profile, shown in
  // the top bar so a reviewer can see who they are signed in as (BL-387).
  email?: string;
  // Dev mode only: a random ID for one explicit sign-in, so the local stack
  // exercises the same per-login "since your last visit" window as Cognito.
  sessionKey?: string;
}

const STORAGE_KEY = 'verityrx.dash.identity';

export const AUTH_MODE: AuthMode =
  (import.meta.env.VITE_AUTH_MODE as AuthMode | undefined) ?? 'dev';

export const API_BASE: string = import.meta.env.VITE_API_BASE ?? '/v1/dash';

let current: Identity | null = null;

export function loadIdentity(): Identity | null {
  if (current) return current;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    current = JSON.parse(raw) as Identity;
    return current;
  } catch {
    return null;
  }
}

export function saveIdentity(identity: Identity): void {
  current = identity;
  localStorage.setItem(STORAGE_KEY, JSON.stringify(identity));
}

export function clearIdentity(): void {
  current = null;
  localStorage.removeItem(STORAGE_KEY);
}

// Read-only accessor for the API client (no React dependency).
export function getIdentity(): Identity | null {
  return current ?? loadIdentity();
}

export function isInternalAdmin(identity: Identity | null): boolean {
  if (!identity) return false;
  return ['internal_admin', 'owner', 'admin', 'compliance_officer'].includes(
    identity.role,
  );
}
